import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { WebsocketProvider } from 'y-websocket'
import { DEFAULT_POINTER_COLOR, POINTER_COLORS, type Frame, type Presence, type Rect } from '@praesto/shared'
import type { CameraStore } from '../canvas/CameraStore.js'
import type { RemotePresence } from '../collab/usePresence.js'

export interface Presentation {
  /** Jemand anderes präsentiert gerade. */
  presenter: RemotePresence | null
  /** Ich präsentiere. */
  isPresenting: boolean
  /** Folge ich der Ansicht des Präsentierenden? */
  following: boolean
  /** Gäste und Mitleser, deren tatsächlichen Ausschnitt der Präsentierende spiegeln kann. */
  viewers: RemotePresence[]
  frameIndex: number
  /** Darf sich der Präsentierende im Vortrag frei bewegen? */
  freeNavigation: boolean
  toggleFreeNavigation: () => void
  /** Farbe des eigenen Zeigers; `null` heißt: kein Zeiger, es wird nichts gesendet. */
  pointer: string | null
  togglePointer: () => void
  setPointerColor: (color: string) => void
  start: () => void
  stop: () => void
  follow: () => void
  breakAway: () => void
  goToFrame: (index: number) => void
  next: () => void
  previous: () => void
}

/** Luft um einen gezeigten Frame — bei allen Beteiligten gleich. */
export const FOCUS_PADDING = 1.04

/**
 * Die zuletzt gewählte Zeigerfarbe überdauert den Vortrag.
 *
 * Im Browser des Vortragenden und nicht am Board: Die Farbe ist eine Vorliebe
 * der Person, kein Inhalt der Präsentation — zwei Leute, die dasselbe Board
 * zeigen, dürfen verschiedene haben.
 */
const POINTER_COLOR_KEY = 'praesto.pointerColor'

function readPointerColor(): string {
  try {
    const stored = localStorage.getItem(POINTER_COLOR_KEY)
    if (stored && (POINTER_COLORS as readonly string[]).includes(stored)) return stored
  } catch {
    // Kein Zugriff auf den Speicher (privater Modus) — dann eben die Vorgabe.
  }
  return DEFAULT_POINTER_COLOR
}

const sameRect = (a: Rect | null, b: Rect | null): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    Math.abs(a.x - b.x) < 0.5 &&
    Math.abs(a.y - b.y) < 0.5 &&
    Math.abs(a.w - b.w) < 0.5 &&
    Math.abs(a.h - b.h) < 0.5)

/**
 * Präsentationsmodus.
 *
 * Es kommt bewusst keine eigene Infrastruktur dazu: alles läuft über den
 * ephemeren Awareness-Kanal, der ohnehin schon die Live-Cursor trägt.
 *
 * Übertragen werden zwei Rechtecke in Board-Koordinaten, nie eine Kamera:
 *
 * - `focus` — der Frame, der gerade gezeigt wird. Hat Vorrang. Jeder Gast passt
 *   ihn vollständig in sein eigenes Fenster ein und sieht dadurch garantiert den
 *   ganzen Frame, egal welches Seitenverhältnis oder welche Fenstergröße er hat.
 * - `viewport` — der Bildschirmausschnitt des Präsentierenden. Gilt, solange er
 *   frei navigiert und gerade keinen Frame zeigt.
 *
 * Den Frame zu übertragen statt nur den Ausschnitt ist der Unterschied zwischen
 * „ungefähr dasselbe Bild" und „garantiert derselbe Inhalt": der Ausschnitt des
 * Präsentierenden ist bei abweichendem Seitenverhältnis größer als der Frame,
 * und wer den kopiert, bekommt den Frame kleiner — und in der Gegenrichtung
 * angeschnitten.
 */
export function usePresentation(
  provider: WebsocketProvider | null,
  presence: RemotePresence[],
  camera: CameraStore,
  frames: Frame[],
): Presentation {
  const [isPresenting, setIsPresenting] = useState(false)
  const [following, setFollowing] = useState(true)
  const [frameIndex, setFrameIndex] = useState(0)
  /*
   * Im Vortrag ist die Ansicht standardmäßig festgesetzt.
   *
   * Ein versehentlicher Scroll auf der Bühne verschiebt sonst das Bild aller
   * Zuschauer. Wer bewusst frei zeigen will, gibt die Bewegung über das Schloss
   * frei — dann folgen die Gäste wieder dem Ausschnitt statt dem Frame.
   */
  const [freeNavigation, setFreeNavigation] = useState(false)
  /*
   * Der Zeiger ist an, sobald der Vortrag beginnt, und die zuletzt gewählte
   * Farbe bleibt über Vorträge hinweg erhalten.
   *
   * An als Vorgabe, weil auf etwas zu deuten der Normalfall ist. Wer ihn nicht
   * will, drückt einmal Z — und dann wird auch nichts mehr übertragen.
   */
  const [pointer, setPointer] = useState<string | null>(null)
  const preferredColor = useRef<string>(readPointerColor())
  const lastApplied = useRef<Rect | null>(null)
  const lastFocus = useRef<Rect | null>(null)
  /**
   * Ob dieser Client überhaupt schon einmal eine fremde Ansicht übernommen hat.
   *
   * Nur der allererste Sprung (beim Dazustoßen) darf hart sein — danach ist
   * jeder Wechsel eine Fahrt. Das an `lastFocus` festzumachen war zu wenig:
   * wird `focus` zwischendurch einmal geleert und neu gesetzt, sähe der Wechsel
   * wieder wie ein Beitritt aus und spränge hart.
   */
  const hasAdopted = useRef(false)

  const presenter = useMemo(
    () => presence.find((remote) => remote.presenting) ?? null,
    [presence],
  )
  const viewers = useMemo(
    () => presence.filter((remote) => !remote.presenting && remote.viewport),
    [presence],
  )

  /*
   * Der Ansicht des Präsentierenden folgen.
   *
   * Das hängt bewusst direkt am Awareness-Kanal und nicht am gerenderten
   * `presence`-Zustand: eine Kamerafahrt des Präsentierenden erzeugt rund 36
   * Aktualisierungen in 600 ms. Liefe jede davon durch einen React-Render,
   * bliebe die Ansicht des Gastes sichtbar hinter der des Präsentierenden
   * zurück. So wird die Kamera synchron gesetzt — dieselbe Trennung wie beim
   * Zoomen, das ebenfalls keinen Render auslöst.
   */
  useEffect(() => {
    const awareness = provider?.awareness
    if (!awareness || isPresenting || !following) return

    const apply = (): void => {
      let focus: Rect | null = null
      let viewport: Rect | null = null
      awareness.getStates().forEach((state, clientId) => {
        if (clientId === awareness.clientID) return
        const remote = state as Partial<Presence>
        if (!remote?.presenting) return
        focus = remote.focus ?? null
        viewport = remote.viewport ?? null
      })

      if (focus) {
        // Ein Frame ändert sich nur beim Wechsel — nicht 60-mal pro Sekunde.
        // Deshalb wird hier animiert statt hart gesetzt: der Gast erlebt
        // dieselbe Fahrt wie der Präsentierende, ohne dass jeder Zwischenschritt
        // über das Netz muss. Beim Dazustoßen wird ohne Animation eingesprungen.
        if (sameRect(lastFocus.current, focus)) return
        const first = !hasAdopted.current
        hasAdopted.current = true
        lastFocus.current = focus
        lastApplied.current = focus
        camera.animateToRect(focus, FOCUS_PADDING, first ? 0 : 600, 0)
        return
      }

      lastFocus.current = null
      // Ohne diesen Vergleich würde jede fremde Mausbewegung — die ebenfalls
      // ein Awareness-Update ist — die Kamera neu setzen.
      if (!viewport || sameRect(lastApplied.current, viewport)) return
      hasAdopted.current = true
      lastApplied.current = viewport
      camera.adoptViewport(viewport)
    }

    apply()
    awareness.on('change', apply)
    return () => awareness.off('change', apply)
  }, [camera, following, isPresenting, provider])

  // Sobald niemand mehr präsentiert, ist der Folgemodus für das nächste Mal
  // wieder scharf.
  useEffect(() => {
    if (!presenter) {
      lastApplied.current = null
      lastFocus.current = null
      hasAdopted.current = false
      setFollowing(true)
    }
  }, [presenter])

  // Den eigenen Folgestatus melden, damit der Präsentierende sieht, wer
  // tatsächlich mitschaut und wer sich gerade selbst umsieht.
  useEffect(() => {
    provider?.awareness.setLocalStateField('following', following && !isPresenting)
  }, [following, isPresenting, provider])

  const setFocus = useCallback(
    (rect: Rect | null) => {
      provider?.awareness.setLocalStateField('focus', rect)
    },
    [provider],
  )

  // Den Zeiger melden. Steht er auf `null`, hört auch der Cursor auf zu senden —
  // das übernimmt der Canvas, der den Zustand hier abliest.
  useEffect(() => {
    provider?.awareness.setLocalStateField('pointer', pointer)
    // Beim Ausschalten sofort auch die letzte Position zurücknehmen: Sonst
    // bliebe der Zeiger dort stehen, wo die Maus zuletzt war, bis sie sich
    // wieder bewegt.
    if (isPresenting && pointer === null) {
      provider?.awareness.setLocalStateField('cursor', null)
    }
  }, [isPresenting, pointer, provider])

  const togglePointer = useCallback(() => {
    setPointer((current) => (current === null ? preferredColor.current : null))
  }, [])

  const setPointerColor = useCallback((color: string) => {
    preferredColor.current = color
    setPointer(color)
    try {
      localStorage.setItem(POINTER_COLOR_KEY, color)
    } catch {
      // Ohne Speicher gilt die Farbe eben nur für diesen Vortrag.
    }
  }, [])

  const goToFrame = useCallback(
    (index: number) => {
      const frame = frames[index]
      if (!frame) return
      setFrameIndex(index)
      camera.animateToRect(frame, FOCUS_PADDING)

      if (isPresenting) {
        // Der Frame selbst geht an die Gäste, nicht der eigene Bildausschnitt.
        setFocus({ x: frame.x, y: frame.y, w: frame.w, h: frame.h })
      } else {
        // Wer nicht präsentiert, navigiert für sich — und folgt damit nicht mehr.
        setFollowing(false)
      }
    },
    [camera, frames, isPresenting, setFocus],
  )

  const setPresenting = useCallback(
    (value: boolean) => {
      provider?.awareness.setLocalStateField('presenting', value)
      setIsPresenting(value)
    },
    [provider],
  )

  const start = useCallback(() => {
    setPresenting(true)
    setFollowing(false)
    setFrameIndex(0)
    setFreeNavigation(false)
    setPointer(preferredColor.current)
    // Mit dem ersten Frame beginnen, sofern es einen gibt — sonst zeigte der
    // Zähler „1/N", während die Ansicht noch irgendwo anders steht.
    const first = frames[0]
    if (first) {
      camera.animateToRect(first, FOCUS_PADDING)
      setFocus({ x: first.x, y: first.y, w: first.w, h: first.h })
    } else {
      setFocus(null)
    }
  }, [camera, frames, setFocus, setPresenting])

  const stop = useCallback(() => {
    setFocus(null)
    setPointer(null)
    setPresenting(false)
  }, [setFocus, setPresenting])

  /**
   * Eigene Navigation. Für Zuschauer heißt das „Folgemodus verlassen", für den
   * Präsentierenden „ich zeige gerade keinen Frame mehr" — ab da folgen die
   * Gäste wieder seinem freien Ausschnitt.
   */
  const breakAway = useCallback(() => {
    setFollowing(false)
    if (isPresenting) {
      lastFocus.current = null
      setFocus(null)
    }
  }, [isPresenting, setFocus])

  // Beim Verlassen der Seite die Präsentation abmelden, sonst hängen Gäste an
  // einem Viewport fest, der sich nie wieder ändert.
  useEffect(() => {
    if (!isPresenting) return
    return () => {
      provider?.awareness.setLocalStateField('presenting', false)
      provider?.awareness.setLocalStateField('focus', null)
      provider?.awareness.setLocalStateField('pointer', null)
    }
  }, [isPresenting, provider])

  const next = useCallback(
    () => goToFrame(Math.min(frames.length - 1, frameIndex + 1)),
    [frameIndex, frames.length, goToFrame],
  )
  const previous = useCallback(
    () => goToFrame(Math.max(0, frameIndex - 1)),
    [frameIndex, goToFrame],
  )

  return {
    presenter,
    isPresenting,
    following,
    viewers,
    frameIndex,
    freeNavigation,
    toggleFreeNavigation: () => setFreeNavigation((value) => !value),
    pointer,
    togglePointer,
    setPointerColor,
    start,
    stop,
    follow: () => setFollowing(true),
    breakAway,
    goToFrame,
    next,
    previous,
  }
}
